// /team - Sub-agent team management for cofounders.
// Feature 9.4. Invite agents, assign clients, configure commissions.
// Gated to Silver+ per MSA Section 3(d): "Tier 1 Starter is solo only;
// agent hiring unlocks at Tier 2 Silver."

import { useEffect, useMemo, useState } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { useAuth } from '../hooks/useAuth';
import {
  Card,
  Button,
  Badge,
  statusToBadgeVariant,
  Input,
  Select,
  EmptyState,
  colors,
  headingStyle,
  pageWrapperStyle,
  mutedStyle,
  labelBlockStyle,
  linkStyle,
  useToast,
} from '../components/ui';
import { useLang, fmt } from '../i18n';

interface AgentRow {
  id: string;
  user_id: string;
  role: string;
  custom_commission_pct: number | null;
  cert_status: string;
  shadow_count: number;
  copilot_count: number;
  solo_close_count: number;
  joined_at: string;
  terminated_at: string | null;
  termination_reason: string | null;
  // joined via invite name
  display_name?: string;
  assigned_client_count?: number;
}

interface InviteForm {
  name: string;
  email: string;
  classification: string;
  commission_pct: number;
}

// Tiers that unlock Team management (Silver+ per MSA Section 3(d))
const TEAM_ELIGIBLE_TIERS = new Set(['silver', 'gold', 'elite']);

function agentLabel(agent: AgentRow): string {
  return agent.display_name ?? `Agent ${agent.user_id.substring(0, 8)}`;
}

function TeamLockedView() {
  const { t } = useLang();
  return (
    <PortalLayout>
      <div style={pageWrapperStyle}>
        <h1 style={headingStyle}>{t.team.title}</h1>
        <EmptyState
          title={t.team.lockedTitle}
          description={t.team.lockedDesc}
          action={<a href="/dashboard" style={linkStyle}>{t.team.backLink}</a>}
        />
      </div>
    </PortalLayout>
  );
}

export default function Team() {
  const { t } = useLang();
  const auth = useAuth();
  const toast = useToast();
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState<InviteForm>({
    name: '',
    email: '',
    classification: 'other',
    commission_pct: 15,
  });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [tier, setTier] = useState<string | null>(null);
  const [tierLoading, setTierLoading] = useState(true);

  // Fetch tier + agents in parallel; discard agents if tier is ineligible
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settingsRes, teamRes] = await Promise.all([
          fetch('/api/partner/settings', { credentials: 'same-origin' }),
          fetch('/api/partner/team'),
        ]);

        let resolvedTier = 'starter';
        if (settingsRes.ok) {
          const sj = (await settingsRes.json()) as { qualification_tier?: string };
          resolvedTier = sj.qualification_tier ?? 'starter';
        }
        if (!cancelled) { setTier(resolvedTier); setTierLoading(false); }

        // Only use team data if tier is eligible
        if (TEAM_ELIGIBLE_TIERS.has(resolvedTier)) {
          if (!teamRes.ok) {
            if (!cancelled) { toast.show('error', fmt(t.common.httpError, { status: String(teamRes.status) })); setLoading(false); }
            return;
          }
          const body = (await teamRes.json()) as { agents?: AgentRow[] } | AgentRow[];
          const data = Array.isArray(body) ? body : (body.agents ?? []);
          if (!cancelled) { setAgents(data); setLoading(false); }
        } else {
          if (!cancelled) setLoading(false);
        }
      } catch {
        if (!cancelled) { toast.show('error', t.common.networkError); setTierLoading(false); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleInvite = async () => {
    setInviting(true);
    setInviteResult(null);
    try {
      const res = await fetch('/api/partner/team/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inviteForm),
      });
      if (res.ok) {
        const data = await res.json() as { invite_url: string };
        setInviteResult(data.invite_url);
        setShowInvite(false);
        setInviteForm({ name: '', email: '', classification: 'other', commission_pct: 15 });
        toast.show('success', t.team.inviteSuccess);
      } else {
        toast.show('error', t.team.inviteFailed);
      }
    } catch {
      toast.show('error', t.team.inviteFailed);
    } finally {
      setInviting(false);
    }
  };

  const handleTerminate = async (agentId: string) => {
    try {
      const res = await fetch(`/api/partner/team/${agentId}/terminate`, { method: 'POST' });
      if (!res.ok) { toast.show('error', t.team.terminateFailed); return; }
      setAgents(prev => prev.map(a =>
        a.id === agentId ? { ...a, terminated_at: new Date().toISOString(), cert_status: 'revoked' } : a
      ));
    } catch {
      toast.show('error', t.team.terminateFailed);
    }
  };

  const handleCommissionUpdate = async (agentId: string, pct: number) => {
    try {
      const res = await fetch(`/api/partner/team/${agentId}/commission`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commission_pct: pct }),
      });
      if (!res.ok) { toast.show('error', t.team.commissionUpdateFailed); return; }
      setAgents(prev => prev.map(a =>
        a.id === agentId ? { ...a, custom_commission_pct: pct } : a
      ));
    } catch {
      toast.show('error', t.team.commissionUpdateFailed);
    }
  };

  const activeAgents = useMemo(() => agents.filter(a => !a.terminated_at), [agents]);
  const terminatedAgents = useMemo(() => agents.filter(a => a.terminated_at), [agents]);

  // AiLys staff/admin bypass tier gate
  const isInternalRole = auth.role === 'ailys_staff' || auth.role === 'ailys_admin';

  if (tierLoading) {
    return (
      <PortalLayout>
        <div style={pageWrapperStyle}>
          <div style={mutedStyle}>{t.common.loading}</div>
        </div>
      </PortalLayout>
    );
  }

  if (!isInternalRole && !TEAM_ELIGIBLE_TIERS.has(tier ?? '')) {
    return <TeamLockedView />;
  }

  return (
    <PortalLayout>
      <div style={pageWrapperStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ ...headingStyle, margin: '0 0 4px' }}>{t.team.title}</h1>
            <p style={mutedStyle}>{t.team.subtitle}</p>
          </div>
          <Button
            variant={showInvite ? 'ghost' : 'secondary'}
            onClick={() => setShowInvite(!showInvite)}
          >
            {showInvite ? t.common.cancel : t.team.inviteBtn}
          </Button>
        </div>

        {inviteResult && (
          <Card style={{ borderColor: colors.successBorder }}>
            <div style={{ color: colors.successAlt, fontSize: '0.85rem', marginBottom: '8px' }}>{t.team.inviteSuccess}</div>
            <code style={{ color: colors.cyan, fontSize: '0.85rem', wordBreak: 'break-all' }}>{inviteResult}</code>
          </Card>
        )}

        {showInvite && (
          <Card size="lg">
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: '0 0 16px', color: colors.text }}>{t.team.inviteTitle}</h2>
            <Input
              label={t.team.fieldName}
              value={inviteForm.name}
              onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t.team.fieldNamePlaceholder}
            />
            <Input
              label={t.team.fieldEmail}
              value={inviteForm.email}
              onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
              placeholder={t.team.fieldEmailPlaceholder}
              type="email"
            />
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <Select
                  label={t.team.fieldClassification}
                  value={inviteForm.classification}
                  onChange={e => setInviteForm(f => ({ ...f, classification: e.target.value }))}
                  options={[
                    { value: 'other', label: t.team.classOther },
                    { value: 'w2', label: t.team.classW2 },
                    { value: 't4a', label: t.team.classT4a },
                  ]}
                />
              </div>
              <div style={{ flex: 1, minWidth: '150px' }}>
                <Input
                  label={t.team.fieldCommission}
                  type="number"
                  min={5}
                  max={25}
                  value={inviteForm.commission_pct}
                  onChange={e => setInviteForm(f => ({ ...f, commission_pct: Number(e.target.value) }))}
                />
              </div>
            </div>
            <Button variant="primary" busy={inviting} onClick={handleInvite}>
              {t.team.sendInvite}
            </Button>
          </Card>
        )}

        {loading && <div style={mutedStyle}>{t.team.loadingTeam}</div>}

        {!loading && activeAgents.length === 0 && !showInvite && (
          <EmptyState
            title={t.team.emptyTitle}
            description={t.team.emptyDesc}
          />
        )}

        {activeAgents.length > 0 && (
          <Card size="lg">
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: '0 0 16px', color: colors.text }}>
              {fmt(t.team.activeAgents, { count: activeAgents.length })}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {activeAgents.map(agent => (
                <div
                  key={agent.id}
                  style={{
                    padding: '16px',
                    borderRadius: '8px',
                    background: colors.cardBgLight,
                    border: `1px solid ${colors.borderSubtle}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, color: colors.text }}>
                        {agentLabel(agent)}
                      </div>
                      <div style={{ ...mutedStyle, fontSize: '0.75rem', marginTop: '4px' }}>
                        {fmt(t.team.joined, { date: new Date(agent.joined_at).toLocaleDateString() })}
                      </div>
                    </div>
                    <Badge variant={statusToBadgeVariant(agent.cert_status)}>
                      {agent.cert_status.replace('_', ' ')}
                    </Badge>
                  </div>

                  <div style={{ display: 'flex', gap: '24px', marginTop: '12px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ ...labelBlockStyle, marginBottom: '2px' }}>{t.team.commission}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="number"
                          min={5}
                          max={25}
                          defaultValue={agent.custom_commission_pct ?? 15}
                          onBlur={e => {
                            const v = Number(e.target.value);
                            if (!Number.isNaN(v) && v >= 5 && v <= 25) {
                              handleCommissionUpdate(agent.id, v);
                            }
                          }}
                          style={{
                            width: '80px',
                            padding: '10px 14px',
                            borderRadius: '8px',
                            border: `1px solid ${colors.borderLight}`,
                            background: colors.cardBgLight,
                            color: colors.text,
                            fontSize: '0.85rem',
                            boxSizing: 'border-box',
                            outline: 'none',
                            textAlign: 'center',
                          }}
                        />
                        <span style={{ color: colors.mutedLight, fontSize: '0.8rem' }}>%</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ ...labelBlockStyle, marginBottom: '2px' }}>{t.team.assignedClients}</div>
                      <div style={{ color: colors.text, fontSize: '1.1rem', fontWeight: 600 }}>
                        {agent.assigned_client_count ?? 0}
                      </div>
                    </div>
                    <div>
                      <div style={{ ...labelBlockStyle, marginBottom: '2px' }}>{t.team.shadowCopilotSolo}</div>
                      <div style={{ color: colors.text, fontSize: '0.9rem' }}>
                        {agent.shadow_count} / {agent.copilot_count} / {agent.solo_close_count}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <Button
                      variant="danger"
                      onClick={() => handleTerminate(agent.id)}
                      style={{ fontSize: '0.75rem', padding: '6px 16px' }}
                    >
                      {t.team.terminate}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {terminatedAgents.length > 0 && (
          <Card size="lg">
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: '0 0 16px', color: colors.mutedLight }}>
              {fmt(t.team.terminatedSection, { count: terminatedAgents.length })}
            </h2>
            {terminatedAgents.map(agent => (
              <div
                key={agent.id}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  background: 'rgba(15, 23, 42, 0.2)',
                  marginBottom: '8px',
                  opacity: 0.6,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: colors.muted, fontSize: '0.85rem' }}>
                    {agentLabel(agent)}
                  </span>
                  <span style={{ color: colors.mutedLight, fontSize: '0.75rem' }}>
                    {fmt(t.team.terminatedOn, {
                      date: agent.terminated_at ? new Date(agent.terminated_at).toLocaleDateString() : '',
                    })}
                  </span>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </PortalLayout>
  );
}
